import { useForm } from "@website/form/hook/form.hooks";
import { CheckboxFilter } from "@website/widget/filter/filter-components/checkbox";
import { RangeFilter } from "@website/widget/filter/filter-components/range";
import { useFilters } from "@website/widget/filter/filter.hook";
import { getQuery } from "@website/widget/filter/helpers";
import { HotelFilterChipsPropertiesTypes } from "@website/widget/filter/hotel-filter-chips/hotel-filter-chips.types";
import omit from "lodash-es/omit";
import { Subscription } from "maplibre-gl";
import { useRouter } from "next/router";
import { MouseEvent, useCallback, useEffect } from "react";

export const useHotelFiltersChips = <T extends Record<string, unknown>>({
  chipsFiltersSchema,
  mode,
  validations,
  onChange
}: HotelFilterChipsPropertiesTypes<T>) => {
  const router = useRouter();

  const { setFilterQueries } = useFilters({
    schema: chipsFiltersSchema
  });

  const methods = useForm<T>({
    mode,
    validations
  });

  useEffect(() => {
    const watcher: Subscription = methods.watch((values, object) => {
      const [name] = object.name?.split(".") ?? [];
      const schema = (values as T)[name] as {
        type: string;
        value: unknown;
        withDebounce?: boolean;
      };

      const parameters = {
        name,
        asQuery: getQuery(schema),
        withDebounce: schema?.withDebounce
      };
      setFilterQueries(parameters)?.then(() => onChange?.(parameters));
    });

    return watcher.unsubscribe;
  }, [methods, onChange, setFilterQueries]);

  const filtersSchemaEntries = Object.entries(chipsFiltersSchema);
  const children = filtersSchemaEntries
    .map(([name, schema]) => {
      switch (schema.type) {
        case "range": {
          return <RangeFilter key={name} {...schema} showTitle={false} />;
        }
        case "checkbox": {
          return (
            <CheckboxFilter
              collapseClassName="!overflow-auto"
              containerClassName={schema?.containerClassName}
              key={name}
              name={name}
              {...schema}
              isNew={false}
              showTitle={false}
            />
          );
        }
        default: {
          return null;
        }
      }
    })
    .filter(Boolean);

  const onClick = useCallback(
    (filterKeys: Array<string>) =>
      router.replace(
        {
          pathname: router?.pathname,
          query: {
            ...router?.query,
            popover: filterKeys?.length > 1 ? "price" : filterKeys?.[0]
          }
        },
        undefined,
        { shallow: true, scroll: false }
      ),
    [router]
  );

  const onClose = useCallback(
    (filterKeys: Array<string>, event?: React.MouseEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      return router.replace(
        {
          pathname: router?.pathname,
          query: omit(router?.query, [...filterKeys, "popover"])
        },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [router]
  );

  const onReset = useCallback(() => {
    const currentPopover = String(router?.query?.["popover"]);
    return onClose([
      currentPopover,
      ...(currentPopover === "price_range" ? ["price_from", "price_to"] : [])
    ]);
  }, [onClose, router?.query]);

  return {
    filtersSchemaEntries,
    children,
    methods,
    onClick,
    onClose,
    onSubmit: (event: MouseEvent<HTMLButtonElement>) => onClose([], event),
    onReset
  };
};
